import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { ThreadArt } from "@/components/ThreadArt";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { PILLARS } from "@/data/pillars";
import { Sigil } from "@/components/Sigil";
import { IMG } from "@/data/images";
import { useSEO } from "@/components/Seo";
import { btnGold, btnGhost, eyebrow } from "@/lib/styles";

export default function Home() {
  useSEO(
    "Threads Unbound™ | Art & Apparel for the Untamed",
    "An art and apparel house built on six pillars: Freedom, Chaos, Truth, Grit, Rebellion, Wisdom. Wearable art for the untamed."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);
  const [featured, setFeatured] = useState([]);
  const [wallPreview, setWallPreview] = useState("");

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setFeatured((r.data || []).filter((p) => p.featured).slice(0, 3)))
      .catch(() => {});
    api
      .get("/artworks")
      .then((r) => {
        const tp = (r.data || []).find((a) => a.title === "Trust the Process");
        if (tp?.wallpaper_preview_url) setWallPreview(tp.wallpaper_preview_url);
      })
      .catch(() => {});
  }, []);

  return (
    <main>
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
        <motion.div style={{ y: bgY, opacity: fade }} className="absolute inset-0">
          <img
            src="/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/2fbfd05f-b62b-4f83-916b-d0b3d17c8bc8.jpg"
            alt="Trust the Process, Threads Unbound original artwork"
            className="h-[120%] w-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(10,10,11,0.35)_15%,#0A0A0B_95%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,#0A0A0B_0%,transparent_45%)]" />
        </motion.div>
        <div className="relative z-10 w-full px-6 pb-24 pt-32 md:px-12 lg:px-20">
          <MaskedLine delay={0.15}>
            <span className={eyebrow}>Life hits. We create.</span>
          </MaskedLine>
          <h1 className="mt-8 font-serif leading-[0.88] tracking-tighter">
            <MaskedLine delay={0.3}>
              <span className="block text-[16vw] font-light md:text-[11vw]">THREADS</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="block text-[16vw] font-light italic text-[#D4AF37] md:text-[11vw]">
                UNBOUND<span className="align-top text-[4vw] not-italic text-white/50">™</span>
              </span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-10 max-w-md text-base leading-relaxed text-white/60 md:text-lg"
          >
            Six pillars. One thread. Wearable art for the untamed.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/shop" data-testid="hero-shop-cta" className={btnGold}>
              Shop the Collection
            </Link>
            <Link to="/join" data-testid="hero-join-cta" className={btnGhost}>
              Join the Movement
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 z-10 hidden md:block"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
            <ArrowDown className="h-5 w-5 text-[#D4AF37]/70" />
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee items={["FREEDOM", "CHAOS", "TRUTH", "GRIT", "REBELLION", "WISDOM"]} />

      <section className="px-6 py-28 md:px-12 md:py-40 lg:px-20" data-testid="brand-statement">
        <Reveal>
          <p className="max-w-5xl font-serif text-3xl font-light leading-snug text-white/85 md:text-5xl md:leading-tight">
            Threads Unbound™ is an art house built on{" "}
            <span className="italic text-[#D4AF37]">six pillars</span>. Every piece starts as a drawing in the dark and
            ends as something you wear like a <span className="italic text-[#D4AF37]">conviction</span>.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/story"
            data-testid="statement-story-link"
            className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50 transition-colors duration-300 hover:text-[#D4AF37]"
          >
            Read our story <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {featured.length > 0 && (
        <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="featured-works">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className={eyebrow}>Selected pieces</p>
                <h2 className="mt-4 font-serif text-4xl font-light md:text-5xl">Featured Works</h2>
              </div>
              <Link
                to="/shop"
                data-testid="featured-view-shop"
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50 transition-colors duration-300 hover:text-[#D4AF37]"
              >
                View the shop <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="pillar-journey-cta">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <Reveal>
            <p className={eyebrow}>A small ritual</p>
            <h2 className="mt-4 font-serif text-4xl font-light md:text-5xl">
              Find your pillar. <span className="italic text-[#D4AF37]">Carry it with you.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
              Three questions. Six pillars. Discover artwork that speaks to you, then make it your wallpaper.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/find-your-pillar" data-testid="home-find-pillar-cta" className={btnGold}>
                Find My Pillar
              </Link>
              <Link to="/free-wallpapers" data-testid="home-wallpapers-cta" className={btnGhost}>
                Browse Free Wallpapers
              </Link>
            </div>
          </Reveal>
          {wallPreview && (
            <Reveal delay={0.15}>
              <div className="mx-auto w-full max-w-[240px]">
                <div className="rounded-[2rem] border border-white/15 bg-black p-2 shadow-[0_0_80px_rgba(212,175,55,0.08)]">
                  <img
                    src={wallPreview}
                    alt="Trust the Process phone wallpaper preview"
                    loading="lazy"
                    className="aspect-[9/19] w-full rounded-[1.6rem] object-cover"
                    data-testid="pillar-journey-preview"
                  />
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="pillars-preview">
        <Reveal>
          <p className={eyebrow}>The foundation</p>
          <h2 className="mt-4 font-serif text-4xl font-light md:text-5xl">The Six Pillars</h2>
        </Reveal>
        <div className="mt-14">
          {PILLARS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.04}>
              <Link
                to="/pillars"
                data-testid={`pillar-preview-${p.name.toLowerCase()}`}
                className="group flex items-baseline justify-between gap-6 border-b border-white/10 py-6 md:py-8"
              >
                <div className="flex items-baseline gap-6 md:gap-10">
                  <span className="font-serif text-sm text-[#D4AF37]/70">{p.num}</span>
                  <span className="hidden self-center sm:block">
                    <Sigil pillar={p.name} size={30} strokeWidth={3} />
                  </span>
                  <span className="font-serif text-3xl font-light transition-colors duration-300 group-hover:text-[#D4AF37] md:text-5xl">
                    {p.name}
                  </span>
                </div>
                <span className="hidden max-w-[220px] text-right text-sm text-white/40 lg:block">{p.essence}</span>
                <ArrowRight className="h-5 w-5 shrink-0 text-white/30 transition-all duration-300 group-hover:translate-x-2 group-hover:text-[#D4AF37]" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden px-6 py-32 text-center md:py-44" data-testid="join-cta">
        <img src="/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/6b8f9f4d-c9d1-48ff-baed-8d7428d7206e.jpg" alt="Thank You for the Light, Threads Unbound original artwork" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0B]/60" />
        <Reveal className="relative">
          <h2 className="font-serif text-4xl font-light md:text-6xl">
            Become <span className="italic text-[#D4AF37]">Unbound</span>
          </h2>
          <p className="mx-auto mt-6 max-w-md text-white/60">
            Letters from the studio. First word on every drop. No noise.
          </p>
          <Link to="/join" data-testid="join-cta-button" className={`${btnGold} mt-10`}>
            Join the Community
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
